import React, { useState } from "react";
import { View, Pressable, StyleSheet } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { TIME_MODIFIERS, TIME_MODIFIER_CODES, TimeModifierCode } from "@/types/db/time_cards";

export type TimeModifierEditorProps = {
  selectedModifiers?: TimeModifierCode[];
  onPick: (modifiers: TimeModifierCode[]) => void;
  onCancel?: () => void;
};

export function TimeModifierEditor({ selectedModifiers = [], onPick, onCancel }: TimeModifierEditorProps) {
  const { colors } = useTheme();
  const [selectedIds, setSelectedIds] = useState<TimeModifierCode[]>(selectedModifiers);

  const toggleModifier = (code: TimeModifierCode) => {
    setSelectedIds((prev) => (prev.includes(code) ? prev.filter((x) => x !== code) : [...prev, code]));
  };

  const selectedModifiersList = TIME_MODIFIER_CODES.filter((code) => selectedIds.includes(code));

  return (
    <View testID="time-modifier-editor" style={styles.container}>
      <ThemedText type="subtitle">Time Modifiers</ThemedText>

      {/* Selected chips */}
      {selectedModifiersList.length > 0 && (
        <View style={styles.chipContainer}>
          {selectedModifiersList.map((code) => {
            const modifier = TIME_MODIFIERS[code];
            return (
              <Pressable
                key={code}
                style={[styles.chip, { backgroundColor: modifier.color }]}
                onPress={() => toggleModifier(code)}
              >
                <ThemedText style={styles.chipText}>
                  {code} ({modifier.multiplier}x)
                </ThemedText>
                <MaterialCommunityIcons name="close" size={14} color="white" />
              </Pressable>
            );
          })}
        </View>
      )}

      <View style={styles.optionsList}>
        {TIME_MODIFIER_CODES.map((code) => {
          const modifier = TIME_MODIFIERS[code];
          const isSelected = selectedIds.includes(code);
          return (
            <Pressable
              key={code}
              testID={`modifier-option-${code}`}
              style={[
                styles.itemRow,
                { borderBottomColor: colors.border },
                isSelected && { backgroundColor: modifier.color + "15" },
              ]}
              onPress={() => toggleModifier(code)}
            >
              <MaterialCommunityIcons
                name={isSelected ? "checkbox-marked" : "checkbox-blank-outline"}
                size={24}
                color={isSelected ? modifier.color : colors.text}
              />
              <View style={styles.itemContent}>
                <View style={styles.headerRow}>
                  <ThemedText type="defaultSemiBold">{modifier.label}</ThemedText>
                  <View style={[styles.codeBadge, { backgroundColor: modifier.color }]}>
                    <ThemedText style={styles.codeText}>{code}</ThemedText>
                  </View>
                </View>
                <ThemedText style={[styles.multiplierText, { color: colors.textSecondary }]}>
                  {modifier.multiplier}x multiplier
                </ThemedText>
              </View>
            </Pressable>
          );
        })}
      </View>

      <View style={styles.footer}>
        {onCancel && (
          <Button
            testID="time-modifier-editor-cancel"
            text="Cancel"
            style={[styles.cancelButton, { borderColor: colors.border }]}
            textStyle={{ color: colors.text }}
            onPress={onCancel}
          />
        )}
        <Button
          testID="time-modifier-editor-confirm"
          text={`Confirm${selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}`}
          style={[styles.confirmButton, { backgroundColor: colors.primary }]}
          textStyle={{ color: "white" }}
          onPress={() => onPick(selectedIds)}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 8,
    gap: 8,
    minWidth: 320,
  },
  chipContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  chip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 16,
  },
  chipText: {
    fontSize: 12,
    color: "white",
    fontWeight: "500",
  },
  optionsList: {
    gap: 0,
  },
  itemRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    backgroundColor: "transparent",
  },
  itemContent: {
    flex: 1,
    gap: 2,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  codeBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  codeText: {
    fontSize: 10,
    fontWeight: "700",
    color: "white",
  },
  multiplierText: {
    fontSize: 12,
  },
  footer: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
    marginTop: 8,
  },
  cancelButton: {
    height: 32,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  confirmButton: {
    height: 32,
    paddingHorizontal: 16,
  },
});
